'use client';

import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { hasAuthTokenCookie } from '@/lib/auth/clearClientSessionState';
import { authTokenCookieName } from '@/lib/supabase/sessionCookies';

export const SIGNED_IN_HOME = '/app';

/** Cookie names in a `document.cookie` string. */
function cookieNamesOf(cookieString: string): string[] {
  return cookieString.split(';').map((pair) => pair.trim().split('=')[0]);
}

/**
 * Whether a visitor holding these cookies belongs in the app rather than on the landing
 * page: this project's session cookie (`base`) or its first chunk (`base.0`) is present.
 * Another project's `sb-*-auth-token`, a PKCE `-code-verifier` or no cookie at all keep
 * the visitor on the landing page. No network call: a dead session is sent to `/app` and
 * the proxy routes it to the login page from there.
 */
export function shouldRedirectSignedIn(cookieString: string, base: string | null): boolean {
  if (!base) return false;
  const names = new Set(cookieNamesOf(cookieString));
  return names.has(base) || names.has(`${base}.0`);
}

/**
 * The inline script that runs the same check before the landing page paints. It is
 * rendered ahead of the page content (after the document's providers, which paint
 * nothing), so a signed-in visitor sees nothing of the landing page: the document is
 * hidden and the URL replaced (no history entry). Keep it that way: visible chrome placed
 * above the page content in the public document would paint before the script runs.
 */
export function buildSignedInRedirectScript(base: string): string {
  const names = JSON.stringify([base, `${base}.0`]);
  return (
    '(function(){' +
    "var n=document.cookie.split(';').map(function(p){return p.trim().split('=')[0]});" +
    `var s=${names};` +
    'for(var i=0;i<s.length;i++){if(n.indexOf(s[i])>-1){' +
    "document.documentElement.style.visibility='hidden';" +
    `location.replace('${SIGNED_IN_HOME}');return;}}` +
    '})();'
  );
}

/**
 * Sends a signed-in visitor from the public home page to `/app`.
 *
 * The home page is prerendered, so the server cannot read the session. This renders the
 * inline script for full loads (runs before paint) and an effect for client navigations
 * into the home page (scripts inserted by React do not run).
 */
export function SignedInRedirect() {
  const router = useRouter();

  useEffect(() => {
    if (hasAuthTokenCookie()) {
      router.replace(SIGNED_IN_HOME);
    }
  }, [router]);

  const base = authTokenCookieName();
  if (!base) return null;

  return <script dangerouslySetInnerHTML={{ __html: buildSignedInRedirectScript(base) }} />;
}
